import React, { useRef, useEffect } from 'react'

import { i18n } from '@fs/zion-locale'
import getKiosk from '../../helpers/getKiosk'
import { processPostDockRequests } from '../../helpers/azureAuthServices'
import {
  INIT_KIOSK,
  SETUP_DIRECT_MESSAGE_LINK,
  SET_CONNECTION_STATUS,
  SET_CHINESE_STATUS,
} from './useKioskReducerActions'
import { kioskInitialState, kioskReducer } from './useKioskReducer'
import { useDSCDispatch } from '../../../../state/DSCStore'
import setCookie from '../../../../shared/helpers/cookie'
import useDirectedMessage from '../../../DirectedMessageProvider'
import useDSApplicationMode from '../../../../shared/hooks/useDSApplicationMode'
import { setSessionStorageItem } from '../../../../shared/helpers/sessionStorage'

const useInterval = (callback, delay, ...args) => {
  const savedCallback = useRef()

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    function tick() {
      savedCallback.current(...args)
    }
    if (delay !== null && delay !== undefined) {
      const id = setInterval(tick, delay)
      return () => clearInterval(id)
    }
    return () => {}
  }, [args, delay])
}

const useKiosk = ({ listenForDock = false, listenForUndock = false }) => {
  const [state, dispatch] = React.useReducer(kioskReducer, kioskInitialState)
  const rootDispatch = useDSCDispatch()
  const {
    socket,
    video,
    color,
    redirect,
    isChinese,
    unauthRedirect,
    connectionStatus,
    message,
    kioskQueryParams,
    undockRedirect,
    kioskId,
    tabletId,
    directMessageTargetDeviceId,
    socketURL,
    noDock,
  } = state
  const hasReceivedLatestSelfPing = useRef()
  const delay = listenForDock || listenForUndock ? 10000 : null
  const { respondToDirectedMessage, sendDirectedMessageDetailed } = useDirectedMessage(
    socket,
    directMessageTargetDeviceId
  )

  const applicationMode = useDSApplicationMode()

  useInterval(
    ({ hasReceivedPing }) => {
      if (!hasReceivedPing.current) {
        if (socket?.readyState === 0 || socket?.readyState === 1) {
          socket.close()
        }
        dispatch({
          type: INIT_KIOSK,
          data: { ...getKiosk() },
        })
      }

      hasReceivedPing.current = false
      // Send ping message directed to self
      if (socket?.readyState === 1) {
        const destinationId = tabletId || kioskId
        sendDirectedMessageDetailed({
          socket,
          directedAction: 'ping',
          destinationIds: [destinationId],
        })
      }

      return true
    },
    delay,
    { hasReceivedPing: hasReceivedLatestSelfPing }
  )

  React.useEffect(() => {
    if (listenForDock || listenForUndock) {
      dispatch({
        type: INIT_KIOSK,
        data: { ...getKiosk() },
      })
    }
  }, [listenForDock, listenForUndock])

  React.useEffect(() => {
    if (socket) {
      socket.onopen = () => {
        dispatch({
          type: SET_CONNECTION_STATUS,
          data: 'open and authorized',
        })
        const destinationId = tabletId || kioskId
        sendDirectedMessageDetailed({
          socket,
          directedAction: 'ping',
          destinationIds: [destinationId],
        })
      }
      socket.onmessage = (event) => {
        if (event && event.data) {
          const payload = JSON.parse(event.data)
          if (payload.action === 'aDirectedMessage' && payload.data.directedAction === 'ping') {
            hasReceivedLatestSelfPing.current = true
          } else if (payload.action === 'aDirectedMessage') {
            respondToDirectedMessage(payload)
          } else if (payload.action === 'dock') {
            setSessionStorageItem({ userSessionId: payload.data.sessionId })
            sessionStorage.setItem('userSessionId', payload.data.sessionId)
            // This block can be removed once the old CAF app is decomissioned
            if (applicationMode === 'KIOSK') {
              sessionStorage.setItem('FS.ds-applicationMode', 'CENTER')
              sessionStorage.setItem('FS.ds-dcIdleURL', `${undockRedirect}${kioskQueryParams}`)
              sessionStorage.setItem(
                'FS.ds-socketCommunicationConfig',
                JSON.stringify({
                  kioskId,
                  url: socketURL,
                })
              )
            }
            // End block to remove after CAF decomissioning

            const chineseLocale = payload.data.locale === 'zh-CHS' || payload.data.locale === 'zh-CHT'
            const notChinesableExperiences = ['picture', 'record', 'recordNoTabletUI']
            const navToChinese = !notChinesableExperiences.includes(payload.data.kioskType) && chineseLocale

            const lang = payload?.data?.locale?.split('-')[0] ?? 'en' // Split language from locale, default to english
            i18n.changeLanguage(lang)
            setCookie('fslanguage', lang)

            dispatch({
              type: SET_CHINESE_STATUS,
              data: { isChinese: navToChinese },
            })
            processPostDockRequests({ azureSessionId: payload.data.sessionId, dispatch: rootDispatch })
            dispatch({
              type: SETUP_DIRECT_MESSAGE_LINK,
              data: {
                directMessageTargetDeviceId: applicationMode === 'KIOSK' ? payload.data.tabletId : payload.data.kioskId,
              },
            })
          } else if (payload.action === 'undock') {
            if (listenForUndock) {
              if (applicationMode === 'KIOSK') {
                setCookie('fssessionid', '') // Clean up for kiosk, not controller
              }
              rootDispatch({
                type: 'CLEAR_NONSTANDARD_USER',
              })
              const undockURL =
                applicationMode === 'KIOSK'
                  ? `${undockRedirect}${kioskQueryParams}`
                  : sessionStorage.getItem('FS.ds-undockURL') // FS.ds-undockURL is set in the ipad controller
              window.location = undockURL
            }
          }
        } else {
          // console.log('Unknown problem')
        }
      }
      socket.onerror = () => {
        // console.log(event.data)
      }
      socket.onclose = () => {
        // console.log('closed')
      }
    }
  }, [
    applicationMode,
    kioskId,
    kioskQueryParams,
    listenForUndock,
    respondToDirectedMessage,
    rootDispatch,
    sendDirectedMessageDetailed,
    socket,
    socketURL,
    tabletId,
    undockRedirect,
  ])

  return { socket, video, color, redirect, unauthRedirect, isChinese, connectionStatus, message, noDock }
}
export default useKiosk
